// The device's remembered best on a track — the result screen's yardstick
// (「你的最好成绩 {n} 米」, design §4.4 cold-read finding: without one, "is
// 1558 m good?" has no answer).
//
// H02 B4: it is a **cache of a record the server holds**, never an independent
// claim. Two rules make it honest, and both are here rather than in the result
// screen so they can be tested without a DOM:
//
//   1. Only a run the server verified — and would hand back on the 本周的你
//      rung — may become the remembered best. A run that was rejected, never
//      left the device, was flagged or was interrupted is not a record.
//   2. When the server states its own answer for this track (the 本周的你 rung,
//      §4bis.21), a remembered value the server cannot back loses. Silently
//      showing 「你的最好成绩 1558 米」 next to an empty 本周的你 — the owner's
//      report — is a small dishonesty of the same family H01 removed.
//
// Rule 2 is a mirror, not a ratchet: it also *raises* a cache that lags the
// server (storage cleared, or a run that reached the server through §4.5's
// offline retry, which never passes a result screen). The server's number can
// never be the dishonest one.

const keyFor = (trackId: string): string => `beatme.best.${trackId}`;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null; // storage blocked (private mode, quota) — no yardstick, no lie
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* storage full/blocked — the line is a nicety, never a blocker */
  }
}

/** The remembered best in metres, or 0 when there is none. */
export function readBest(trackId: string): number {
  const n = Number.parseInt(read(keyFor(trackId)) ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** The submit response, as far as this rule cares (`types.ts` shape). */
export interface VerifiedRun {
  scoreM: number;
  /** §3.3 anti-cheat reasons; non-empty ⇒ the rung will never offer this run. */
  flags?: string[];
  /** §4.5 interruption verdict; same consequence. */
  interrupted?: boolean;
}

/**
 * Rule 1 — record a run the **server verified and would offer back**. Called
 * from the submit response, never from the local death frame: an unsubmitted or
 * rejected run has no record to its name.
 *
 * The eligibility test mirrors `selfBestRun`'s (flagged / interrupted are out),
 * because rule 2 only runs inside the `LADDER_PCT` arm: outside it this is the
 * *only* rule, and a yardstick that outlives what the server would show is the
 * whole of B4. Absent fields mean "the server did not say", which is the old
 * wire shape — treated as eligible, exactly as before.
 */
export function recordVerifiedBest(trackId: string, run: VerifiedRun): void {
  const { scoreM } = run;
  if (!Number.isFinite(scoreM) || scoreM <= 0) return;
  if (run.interrupted === true) return;
  if (run.flags !== undefined && run.flags.length > 0) return;
  if (scoreM <= readBest(trackId)) return;
  write(keyFor(trackId), String(Math.trunc(scoreM)));
}

/**
 * Rule 2 — obey the server's own answer for this track (`/api/self-best`,
 * which is the device's best eligible run *including* the one just played).
 *
 * Returns the server's number when it differs from the remembered one (0 = the
 * server has nothing of ours, so the cache is cleared), or `null` when the two
 * already agree and nothing needs saying. A `null` server answer means "no
 * eligible run", which is exactly the case the owner hit after the preview's
 * scratch DB was wiped — never "the request failed": callers must not
 * reconcile against an answer they did not get.
 */
export function reconcileBest(trackId: string, serverBestM: number | null): number | null {
  const stored = readBest(trackId);
  const backed =
    serverBestM !== null && Number.isFinite(serverBestM) && serverBestM > 0
      ? Math.trunc(serverBestM)
      : 0;
  if (backed === stored) return null;
  write(keyFor(trackId), backed > 0 ? String(backed) : null);
  return backed;
}

/** The yardstick line for a result screen, or null when there is nothing
 *  honest to say. `prevBestM` is the remembered best *before* this run. */
export type BestLine = { key: 'result.newBest' | 'result.best'; n: number } | null;

export function bestLine(prevBestM: number, distanceM: number): BestLine {
  if (prevBestM > 0 && distanceM > prevBestM) return { key: 'result.newBest', n: prevBestM };
  if (prevBestM > distanceM) return { key: 'result.best', n: prevBestM };
  return null;
}
